import {
  Badge,
  Body,
  Button,
  Card,
  CardAction,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Separator,
  Title,
} from '@e2n/ui/v2';
import { useTranslation } from 'react-i18next';
import {
  faCalendarRange,
  faEllipsisVertical,
  faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { EmptyCard } from '@/domain/mitarbeiter/features/rotationsplan/features/Plaene/components/EmptyCard';
import {
  useRotationsplanVorlagen,
  type RotationsplanVorlage,
} from '@/domain/mitarbeiter/features/rotationsplan/hooks/useRotationsplanVorlagen/useRotationsplanVorlagen';
import type { CSSProperties } from 'react';

const noop = () => {};

export function VorlagenUebersicht() {
  const { vorlagenByBeruf } = useRotationsplanVorlagen();

  return (
    <div className="flex flex-col items-start gap-6">
      {vorlagenByBeruf.length < 1 ? (
        <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <EmptyCard
            titleKey="domain.mitarbeiter.rotationsplan.vorlagen.keinVorlage.title"
            subtitleKey="domain.mitarbeiter.rotationsplan.vorlagen.keinVorlage.subtitle"
          />
        </div>
      ) : (
        vorlagenByBeruf.map((beruf) => (
          <section key={beruf.id} className="flex w-full flex-col gap-3">
            <div className="flex items-center gap-2">
              <span
                aria-hidden
                className="size-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: beruf.color }}
              />
              <Title asChild>
                <h2>{beruf.name}</h2>
              </Title>
              <Body asChild className="text-labels-secondary">
                <span>{`· ${beruf.kuerzel}`}</span>
              </Body>
            </div>
            <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {beruf.vorlagen.map((vorlage) => (
                <div key={vorlage.id} className="cursor-pointer">
                  <VorlagenCard berufName={beruf.name} vorlage={vorlage} />
                </div>
              ))}
            </div>
          </section>
        ))
      )}
      <Button
        asChild
        iconOnly
        className="absolute right-4 bottom-16 z-49 shadow-xl md:hidden">
        <Link to={`/mitarbeiter/rotationsplaene/vorlagen/create`}>
          <FontAwesomeIcon icon={faPlus} />
        </Link>
      </Button>
    </div>
  );
}

function VorlagenCard({
  vorlage,
  berufName,
}: {
  vorlage: RotationsplanVorlage;
  berufName: string;
}) {
  const { t } = useTranslation();

  return (
    <Card className="h-full gap-3">
      <CardHeader>
        <CardTitle className="line-clamp-2">
          <Body weight="semibold">{`${berufName} - ${vorlage.titel}`}</Body>
        </CardTitle>
        <CardAction>
          <Button
            aria-label={t(
              'domain.mitarbeiter.rotationsplan.vorlagen.mehrOptionen',
            )}
            iconOnly
            size="small"
            type="button"
            variant="ghost"
            onClick={noop}>
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </Button>
        </CardAction>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-2">
        {vorlage.abschnitte.map((abschnitt) => (
          <section key={abschnitt.id} className="flex flex-col gap-1">
            <Body size="sm" className="text-labels-secondary">
              {abschnitt.name}
            </Body>
            <div className="flex min-h-6 flex-wrap items-center gap-1.5">
              {abschnitt.arbeitsbereiche.map((arbeitsbereich) => (
                <Badge
                  key={`${abschnitt.id}-${arbeitsbereich.id}`}
                  layout="character-dot"
                  size="sm"
                  // Der Badge-Punkt ist im UI-Kit fix auf die Brand-Farbe
                  // gesetzt, hier kommt die Farbe aus der Markierung.
                  className="*:data-[slot=badge-dot]:bg-(--vorlage-dot)"
                  style={
                    { '--vorlage-dot': arbeitsbereich.color } as CSSProperties
                  }>
                  {arbeitsbereich.name}
                </Badge>
              ))}
            </div>
          </section>
        ))}
      </CardContent>

      <Separator />

      <CardFooter className="justify-between">
        <Button type="button" variant="outline" onClick={noop}>
          <FontAwesomeIcon icon={faCalendarRange} />
          {t('domain.mitarbeiter.rotationsplan.vorlagen.dauer', {
            count: 20,
          })}
        </Button>
        <Button type="button" variant="secondary-red" onClick={noop}>
          {t('domain.mitarbeiter.rotationsplan.vorlagen.anwenden')}
        </Button>
      </CardFooter>
    </Card>
  );
}
